import { SYNC } from "@station-keeping/contracts";
import {
  applyActionInPlace,
  cloneState,
  hashState,
  isSimError,
  stepInPlace,
  type Action,
  type ScheduledAction,
  type SimErrorCode,
  type StationState,
} from "@station-keeping/sim";

/** Horloge en millisecondes : locale en bac à sable, recalée sur celle du serveur sinon. */
export interface TimeSource {
  now(): number;
}

/** Le tick `tick` correspond à l'instant `at` (ms) : la station avance d'un tick par seconde à partir de là. */
export interface Anchor {
  tick: number;
  at: number;
}

/** Au plus autant de pas par image : au-delà, le rattrapage se poursuit à l'image suivante. */
const MAX_STEPS_PER_FRAME = 600;

/**
 * Au plus autant d'actions datées d'un même tick : un lot se coupe entre deux ticks, et le serveur n'en
 * accepte pas davantage par synchro (personne ne joue aussi vite à la main).
 */
export const MAX_ACTIONS_PER_TICK = SYNC.maxActionsPerSync;

export type DispatchResult = { ok: true } | { ok: false; code: SimErrorCode | "UNKNOWN" };

export type StepListener = (previous: StationState, current: StationState) => void;
export type ActionListener = (scheduled: ScheduledAction) => void;

/**
 * Le moteur du client : il fait avancer la simulation au rythme de l'horloge, applique tout de suite les
 * actions du joueur (le jeu reste réactif) et les garde en file pour le serveur. L'état est muté sur
 * place ; `version` change à chaque pas ou action, c'est ce que suit l'interface.
 */
export class GameEngine {
  state: StationState;
  version = 0;
  paused = false;
  /** Actions jouées, pas encore confirmées par le serveur. */
  outbox: ScheduledAction[] = [];
  /** Empreinte du début de chaque tick où une action en file a été jouée (pour couper un long lot). */
  readonly boundaries = new Map<number, string>();
  private anchor: Anchor;
  private speed = 1;
  private lastSeq: number;
  private boundary: { tick: number; hash: string } | null = null;
  private frame: number | null = null;
  private lagGuard: { ticks: number; handler: () => void } | null = null;
  private readonly listeners = new Set<() => void>();
  private readonly stepListeners = new Set<StepListener>();
  private readonly actionListeners = new Set<ActionListener>();

  constructor(
    state: StationState,
    anchor: Anchor,
    private readonly time: TimeSource,
    lastSeq = 0,
  ) {
    this.state = state;
    this.anchor = anchor;
    this.lastSeq = lastSeq;
  }

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  readonly getVersion = (): number => this.version;

  /** Appelé après chaque série de pas, avec l'état d'avant (copie) et l'état courant. */
  onSteps(listener: StepListener): () => void {
    this.stepListeners.add(listener);
    return () => this.stepListeners.delete(listener);
  }

  /** Appelé après chaque action acceptée et mise en file. */
  onAction(listener: ActionListener): () => void {
    this.actionListeners.add(listener);
    return () => this.actionListeners.delete(listener);
  }

  /**
   * Au-delà de `ticks` de retard (appareil en veille, onglet longtemps caché), le moteur ne rattrape pas
   * seul : il se met en pause et prévient, la partie rouvre alors une session (le serveur compte l'absence).
   */
  setLagGuard(ticks: number, handler: () => void): void {
    this.lagGuard = { ticks, handler };
  }

  private notify(): void {
    this.version += 1;
    for (const listener of this.listeners) listener();
  }

  /** Tick visé à cet instant. */
  targetTick(now: number = this.time.now()): number {
    return this.anchor.tick + Math.floor(((now - this.anchor.at) * this.speed) / 1000);
  }

  /** Ticks de retard sur l'horloge (onglet en arrière-plan, appareil en veille). */
  lag(now: number = this.time.now()): number {
    return Math.max(0, this.targetTick(now) - this.state.tick);
  }

  /** Part du tick en cours déjà écoulée (0 à 1), pour un rendu fluide entre deux pas. */
  alpha(now: number = this.time.now()): number {
    const exact = this.anchor.tick + ((now - this.anchor.at) * this.speed) / 1000;
    return Math.min(1, Math.max(0, exact - this.state.tick));
  }

  /** Millisecondes avant le prochain tick. */
  msToNextTick(now: number = this.time.now()): number {
    const exact = ((now - this.anchor.at) * this.speed) / 1000;
    return Math.max(0, ((Math.floor(exact) + 1 - exact) * 1000) / this.speed);
  }

  /** Fait avancer la station vers le tick visé ; renvoie le nombre de pas joués. */
  advance(now: number = this.time.now()): number {
    if (this.paused) return 0;
    const target = this.targetTick(now);
    if (this.state.tick >= target) return 0;
    if (this.lagGuard && target - this.state.tick > this.lagGuard.ticks) {
      this.setPaused(true);
      this.lagGuard.handler();
      return 0;
    }
    const previous = this.stepListeners.size > 0 ? cloneState(this.state) : this.state;
    let steps = 0;
    while (this.state.tick < target && steps < MAX_STEPS_PER_FRAME) {
      stepInPlace(this.state);
      steps += 1;
    }
    for (const listener of this.stepListeners) listener(previous, this.state);
    this.notify();
    return steps;
  }

  start(): void {
    if (this.frame !== null || typeof requestAnimationFrame === "undefined") return;
    const loop = () => {
      this.advance();
      this.frame = requestAnimationFrame(loop);
    };
    this.frame = requestAnimationFrame(loop);
  }

  stop(): void {
    if (this.frame !== null) cancelAnimationFrame(this.frame);
    this.frame = null;
  }

  /**
   * Empreinte de l'état au début du tick courant, avant toute action datée de ce tick : c'est ce que le
   * serveur compare. Calculée au plus une fois par tick, à la demande.
   */
  boundaryHash(): { tick: number; hash: string } {
    if (!this.boundary || this.boundary.tick !== this.state.tick) {
      this.boundary = { tick: this.state.tick, hash: hashState(this.state) };
    }
    return this.boundary;
  }

  /** Action du joueur : appliquée tout de suite, datée du tick courant, mise en file pour le serveur. */
  dispatch(action: Action): DispatchResult {
    if (this.paused) return { ok: false, code: "UNKNOWN" };
    const tick = this.state.tick;
    if (this.outbox.filter((scheduled) => scheduled.tick === tick).length >= MAX_ACTIONS_PER_TICK) {
      return { ok: false, code: "UNKNOWN" };
    }
    // L'empreinte du tick doit précéder l'action.
    const boundary = this.boundaryHash();
    try {
      applyActionInPlace(this.state, action);
    } catch (error) {
      if (isSimError(error)) return { ok: false, code: error.code };
      throw error;
    }
    this.lastSeq += 1;
    const scheduled = { seq: this.lastSeq, tick, action };
    this.outbox.push(scheduled);
    this.boundaries.set(tick, boundary.hash);
    this.notify();
    for (const listener of this.actionListeners) listener(scheduled);
    return { ok: true };
  }

  /** Le serveur a validé les actions jusqu'au numéro `lastSeq`, et l'état jusqu'au tick `tick`. */
  acknowledge(lastSeq: number, tick: number): void {
    this.outbox = this.outbox.filter((scheduled) => scheduled.seq > lastSeq);
    for (const boundaryTick of this.boundaries.keys()) {
      if (boundaryTick < tick) this.boundaries.delete(boundaryTick);
    }
  }

  /** Reprend un état venu du serveur (ouverture de session, resynchronisation) ; la file est abandonnée. */
  reset(state: StationState, anchor: Anchor, lastSeq: number): void {
    this.state = state;
    this.anchor = anchor;
    this.lastSeq = lastSeq;
    this.outbox = [];
    this.boundaries.clear();
    this.boundary = null;
    this.paused = false;
    this.notify();
  }

  /** Accélère le temps (bac à sable uniquement) en gardant la station à son tick. */
  setSpeed(speed: number, now: number = this.time.now()): void {
    this.anchor = { tick: this.state.tick, at: now };
    this.speed = speed;
    this.notify();
  }

  getSpeed(): number {
    return this.speed;
  }

  getLastSeq(): number {
    return this.lastSeq;
  }

  setPaused(paused: boolean): void {
    this.paused = paused;
    this.notify();
  }
}
